import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  userType: string;
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  universityId?: number;
}

class AuthService {
  private token: string | null = null;

  constructor() {
    // Initialize token from localStorage if available
    this.token = localStorage.getItem('token');
    
    // Set up axios interceptor for authentication
    axios.interceptors.request.use(
      (config) => {
        if (this.token) {
          config.headers.Authorization = `Bearer ${this.token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Set up axios response interceptor to handle auth errors
    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid, clear auth and redirect to login
          this.token = null;
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          // Only redirect if not already on login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  setToken(token: string | null): void {
    this.token = token;
  }

  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, credentials);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle simple error format from AuthController: {"error": "message"}
        if (errorData.error && typeof errorData.error === 'string') {
          throw new Error(errorData.error);
        }
        
        // Handle validation errors from GlobalExceptionHandler: {"errors": {"field": "message"}}
        if (errorData.errors && typeof errorData.errors === 'object' && !Array.isArray(errorData.errors)) {
          const errorMessages = Object.entries(errorData.errors)
            .map(([field, message]) => `${field}: ${message}`)
            .join(', ');
          throw new Error(errorMessages || 'Validation failed');
        }
        
        // Handle general error with message property
        if (errorData.message) {
          throw new Error(errorData.message);
        }
        
        // If error data is a string, use it directly
        if (typeof errorData === 'string') {
          throw new Error(errorData);
        }
        
        // Default to generic message if we can't parse the error
        throw new Error('Login failed. Please check your credentials.');
      }
      
      // Network or other errors
      if (error.message) {
        throw new Error(error.message);
      }
      
      throw new Error('Login failed. Please check your network connection.');
    }
  }

  async getCurrentUser(): Promise<any> {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/me`);
      return response.data;
    } catch (error: any) {
      throw new Error('Failed to get user information');
    }
  }

  async forgotPassword(email: string): Promise<void> {
    try {
      const userType = 'PROVIDER'; // Default to provider for now, can be made dynamic
      await axios.post(`${API_BASE_URL}/auth/forgot-password`, { email, userType });
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to send password reset email');
    }
  }

  async resetPassword(token: string, userType: string, newPassword: string, confirmPassword: string): Promise<void> {
    try {
      await axios.post(`${API_BASE_URL}/auth/reset-password`, {
        token,
        userType,
        newPassword,
        confirmPassword
      });
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Failed to reset password');
    }
  }

  async validateResetToken(token: string, userType: string): Promise<boolean> {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/validate-reset-token`, {
        params: { token, userType }
      });
      return response.data.valid === true;
    } catch (error: any) {
      return false;
    }
  }
}

export const authService = new AuthService();

export const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};